import { useEffect, useMemo, useState } from 'react';
import MentorshipService, {
    EligibleProgramSelection,
    emptyCampusConfigPayload,
    MENTEE_MODES,
    MENTOR_EXPERIENCE_THRESHOLDS,
    MentorshipCampusConfig as MentorshipCampusConfigModel,
    MentorshipCampusConfigPayload,
    MentorshipProgramOption,
} from './MentorshipService';

interface MentorshipCampusConfigProps {
    idPrefix?: string;
    title?: string;
    config?: MentorshipCampusConfigModel | MentorshipCampusConfigPayload | null;
    programs: MentorshipProgramOption[];
    /**
     * Supply both to enable the live eligibility preview while the draft is edited. Omitted by
     * the All Campuses view, which keeps showing the stored counts.
     */
    cohortId?: number | null;
    campusId?: number | null;
    instituteId?: number | null;
    disabled?: boolean;
    saving?: boolean;
    showSave?: boolean;
    saveDisabled?: boolean;
    expanded?: boolean;
    onToggle?: () => void;
    onChange?: (payload: MentorshipCampusConfigPayload, changedKeys?: (keyof MentorshipCampusConfigPayload)[]) => void;
    onSave?: (payload: MentorshipCampusConfigPayload) => void;
    mixedFields?: MentorshipCampusMixedFields;
}

export type MentorshipCampusMixedFields = {
    mentorExperienceThreshold?: boolean;
    maxMenteesPerMentor?: boolean;
    menteeTarget?: boolean;
    eligiblePrograms?: boolean;
    menteeMode?: boolean;
    allowOptOutMidCohort?: boolean;
    allowMentorChangeRequest?: boolean;
};

const VARIES_BY_CAMPUS = 'varies by campus';

const withK12Years = (
    payload: MentorshipCampusConfigPayload,
    programs: MentorshipProgramOption[]
): MentorshipCampusConfigPayload => {
    const byId = new Map(programs.map((program) => [program.programId, program]));
    return {
        ...payload,
        eligiblePrograms: payload.eligiblePrograms.map((item) => {
            const program = byId.get(item.programId);
            if (program && MentorshipService.isK12ProgramLevel(program.programLevel)) {
                return { programId: item.programId, years: MentorshipService.k12AutoYears(program) };
            }
            return item;
        }),
    };
};

const toPayload = (config?: MentorshipCampusConfigModel | MentorshipCampusConfigPayload | null): MentorshipCampusConfigPayload => {
    if (!config) return emptyCampusConfigPayload();
    return {
        mentorExperienceThreshold: config.mentorExperienceThreshold ?? null,
        maxMenteesPerMentor: config.maxMenteesPerMentor ?? null,
        menteeTarget: config.menteeTarget ?? null,
        eligiblePrograms: MentorshipService.normalizeEligiblePrograms(config.eligiblePrograms),
        menteeMode: config.menteeMode ?? null,
        allowOptOutMidCohort: MentorshipService.asBoolean(config.allowOptOutMidCohort),
        allowMentorChangeRequest: MentorshipService.asBoolean(config.allowMentorChangeRequest),
    };
};

const previewUnavailable = (value: number | null | undefined) =>
    value === null || value === undefined ? 'Unavailable' : String(value);

const MentorshipCampusConfig = ({
    idPrefix = 'mentorship-campus',
    title,
    config,
    programs,
    cohortId = null,
    campusId = null,
    instituteId = null,
    disabled = false,
    saving = false,
    showSave = true,
    saveDisabled = false,
    expanded = true,
    onToggle,
    onChange,
    onSave,
    mixedFields = {},
}: MentorshipCampusConfigProps) => {
    const [draft, setDraft] = useState<MentorshipCampusConfigPayload>(toPayload(config));
    const [programSearch, setProgramSearch] = useState('');
    const [livePreview, setLivePreview] = useState<{
        eligibleAlumniCount: number | null;
        eligibleStudentCount: number | null;
    } | null>(null);

    useEffect(() => {
        setDraft(toPayload(config));
        setProgramSearch('');
    }, [config]);

    const update = (
        next: MentorshipCampusConfigPayload,
        changedKeys: (keyof MentorshipCampusConfigPayload)[] = []
    ) => {
        setDraft(next);
        onChange?.(next, changedKeys);
    };

    const neededMentors = MentorshipService.neededMentorsPreview(draft.menteeTarget, draft.maxMenteesPerMentor);
    const stored = config as MentorshipCampusConfigModel & {
        eligible_alumni_count?: number | null;
        eligible_student_count?: number | null;
        confirmed_mentors?: number | null;
    };
    const confirmedMentors = MentorshipService.toNumberOrNull(stored?.confirmedMentors ?? stored?.confirmed_mentors) ?? 0;
    const gap = MentorshipService.gapPreview(neededMentors, confirmedMentors);
    const eligibleAlumniCount = MentorshipService.toNumberOrNull(
        stored?.eligibleAlumniCount ?? stored?.eligible_alumni_count
    );
    const eligibleStudentCount = MentorshipService.toNumberOrNull(
        stored?.eligibleStudentCount ?? stored?.eligible_student_count
    );

    // Live counts for the unsaved draft. The stored counts stay the fallback, so a failed or
    // not-yet-returned preview never invents a number — it shows what was last saved.
    const draftThreshold = draft.mentorExperienceThreshold;
    const draftProgramIds = useMemo(
        () =>
            draft.eligiblePrograms
                .map((item) => item.programId)
                .filter((id): id is number => id != null)
                .sort((a, b) => a - b),
        [draft.eligiblePrograms]
    );
    const draftProgramKey = draftProgramIds.join(',');

    useEffect(() => {
        if (cohortId == null || campusId == null || disabled) {
            setLivePreview(null);
            return;
        }
        let cancelled = false;
        const handle = window.setTimeout(() => {
            MentorshipService.previewCampusEligibility(
                cohortId,
                campusId,
                draftThreshold,
                draftProgramKey === '' ? [] : draftProgramKey.split(',').map(Number),
                instituteId
            )
                .then((result) => {
                    if (!cancelled) setLivePreview(result);
                })
                .catch(() => {
                    if (!cancelled) setLivePreview(null);
                });
        }, 350);
        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [cohortId, campusId, disabled, draftThreshold, draftProgramKey, instituteId]);

    const shownAlumniCount = livePreview ? livePreview.eligibleAlumniCount : eligibleAlumniCount;
    const shownStudentCount = livePreview ? livePreview.eligibleStudentCount : eligibleStudentCount;

    const selectedById = useMemo(() => {
        const map = new Map<number, EligibleProgramSelection>();
        draft.eligiblePrograms.forEach((item) => map.set(item.programId, item));
        return map;
    }, [draft.eligiblePrograms]);

    const visiblePrograms = useMemo(() => {
        const query = programSearch.trim().toLowerCase();
        if (!query) return programs;
        return programs.filter((program) => program.name.toLowerCase().includes(query));
    }, [programs, programSearch]);

    const toggleProgram = (program: MentorshipProgramOption) => {
        if (disabled) return;
        const exists = selectedById.get(program.programId);
        const k12 = MentorshipService.isK12ProgramLevel(program.programLevel);
        const next = exists
            ? draft.eligiblePrograms.filter((item) => item.programId !== program.programId)
            : [
                  ...draft.eligiblePrograms,
                  {
                      programId: program.programId,
                      years: k12 ? MentorshipService.k12AutoYears(program) : [],
                  },
              ];
        update({ ...draft, eligiblePrograms: next }, ['eligiblePrograms']);
    };

    const toggleYear = (programId: number, year: number, program: MentorshipProgramOption) => {
        if (disabled) return;
        if (MentorshipService.isK12ProgramLevel(program.programLevel)) return;
        const current = selectedById.get(programId) || { programId, years: [] };
        const years = current.years.includes(year)
            ? current.years.filter((item) => item !== year)
            : [...current.years, year];
        const next = draft.eligiblePrograms.some((item) => item.programId === programId)
            ? draft.eligiblePrograms.map((item) => (item.programId === programId ? { ...item, years } : item))
            : [...draft.eligiblePrograms, { programId, years }];
        update({ ...draft, eligiblePrograms: next }, ['eligiblePrograms']);
    };

    const body = (
        <>
            <div className="alert alert-primary border-0 rounded-3 mb-3 p-3 d-flex align-items-center flex-wrap gap-2 font-12 text-primary-emphasis bg-primary-subtle">
                <div className="font-700 text-muted">
                    Eligible alumni: {previewUnavailable(shownAlumniCount)}
                    <span className="mx-2 font-500">|</span>
                    Eligible students: {previewUnavailable(shownStudentCount)}
                    <span className="mx-2 font-500">|</span>
                    Confirmed mentors: {confirmedMentors}
                    <span className="mx-2 font-500  ">|</span>
                    Needed mentors: {previewUnavailable(neededMentors)}
                    <span className="mx-2">|</span>
                    Gap: {previewUnavailable(gap)}
                </div>
                {showSave && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-xs font-11 font-600 py-2 px-3 rounded-2 ms-auto"
                        disabled={disabled || saving || saveDisabled}
                        onClick={() => {
                            if (saveDisabled) return;
                            onSave?.(withK12Years(draft, programs));
                        }}>
                        Save
                    </button>
                )}
            </div>

            <fieldset disabled={disabled}>
                <div className="row g-3">
                    <div className="col-md-4">
                        <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor={`${idPrefix}-experience-threshold`}>
                            Mentor Experience Threshold
                        </label>
                        <select
                            id={`${idPrefix}-experience-threshold`}
                            className="form-select font-13"
                            value={draft.mentorExperienceThreshold ?? ''}
                            onChange={(event) =>
                                update(
                                    { ...draft, mentorExperienceThreshold: event.target.value || null },
                                    ['mentorExperienceThreshold']
                                )
                            }>
                            <option value="">
                                {mixedFields.mentorExperienceThreshold ? VARIES_BY_CAMPUS : ''}
                            </option>
                            {MENTOR_EXPERIENCE_THRESHOLDS.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor={`${idPrefix}-max-mentees`}>
                            Max Mentees Per Mentor
                        </label>
                        <input
                            id={`${idPrefix}-max-mentees`}
                            type="number"
                            className="form-control"
                            min={1}
                            max={5}
                            placeholder={mixedFields.maxMenteesPerMentor ? VARIES_BY_CAMPUS : undefined}
                            value={draft.maxMenteesPerMentor ?? ''}
                            onChange={(event) =>
                                update(
                                    {
                                        ...draft,
                                        maxMenteesPerMentor: MentorshipService.toNumberOrNull(event.target.value),
                                    },
                                    ['maxMenteesPerMentor']
                                )
                            }
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor={`${idPrefix}-mentee-target`}>
                            Mentee Target
                        </label>
                        <input
                            id={`${idPrefix}-mentee-target`}
                            type="number"
                            className="form-control"
                            placeholder={mixedFields.menteeTarget ? VARIES_BY_CAMPUS : undefined}
                            value={draft.menteeTarget ?? ''}
                            onChange={(event) =>
                                update(
                                    {
                                        ...draft,
                                        menteeTarget: MentorshipService.toNumberOrNull(event.target.value),
                                    },
                                    ['menteeTarget']
                                )
                            }
                        />
                    </div>

                    <div className="col-12">
                        <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor={`${idPrefix}-program-search`}>
                            Eligible Students for Mentee
                        </label>
                        {mixedFields.eligiblePrograms && (
                            <div className="small text-muted mb-2">{VARIES_BY_CAMPUS}</div>
                        )}
                        <input
                            id={`${idPrefix}-program-search`}
                            type="text"
                            className="form-control mb-2"
                            placeholder="Search programs"
                            value={programSearch}
                            onChange={(event) => setProgramSearch(event.target.value)}
                        />
                        <div className="border rounded p-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
                            {visiblePrograms.length === 0 && <div className="text-muted">No programs</div>}
                            {visiblePrograms.map((program) => {
                                const selected = selectedById.get(program.programId);
                                const isK12 = MentorshipService.isK12ProgramLevel(program.programLevel);
                                const yearOptions =
                                    program.years.length > 0
                                        ? program.years
                                        : isK12
                                          ? MentorshipService.k12AutoYears(program)
                                          : [];
                                const selectedYears =
                                    selected && isK12 ? MentorshipService.k12AutoYears(program) : selected?.years || [];
                                return (
                                    <div key={program.programId} className="mb-3">
                                        <div className="form-check">
                                            <input
                                                id={`${idPrefix}-program-${program.programId}`}
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={Boolean(selected)}
                                                onChange={() => toggleProgram(program)}
                                            />
                                            <label className="form-check-label" htmlFor={`${idPrefix}-program-${program.programId}`}>
                                                {program.name}
                                            </label>
                                        </div>
                                        {selected && yearOptions.length > 0 && (
                                            <div className="d-flex flex-wrap gap-3 ms-4 mt-2">
                                                {yearOptions.map((year) => (
                                                    <div className="form-check" key={year}>
                                                        <input
                                                            id={`${idPrefix}-program-${program.programId}-year-${year}`}
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={selectedYears.includes(year)}
                                                            disabled={isK12}
                                                            onChange={() => toggleYear(program.programId, year, program)}
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={`${idPrefix}-program-${program.programId}-year-${year}`}>
                                                            Year {year}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {selected && yearOptions.length === 0 && (
                                            <div className="small text-muted ms-4 mt-1">Years unavailable</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="col-md-4">
                        <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor={`${idPrefix}-mentee-mode`}>
                            Mentee Mode
                        </label>
                        <select
                            id={`${idPrefix}-mentee-mode`}
                            className="form-select"
                            value={draft.menteeMode ?? ''}
                            onChange={(event) =>
                                update({ ...draft, menteeMode: event.target.value || null }, ['menteeMode'])
                            }>
                            <option value="">{mixedFields.menteeMode ? VARIES_BY_CAMPUS : ''}</option>
                            {MENTEE_MODES.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-4">
                        <div className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1">Allow Mentees to opt-out mid-cohort</div>
                        <div className="d-flex gap-3 mt-1">
                            <div className="form-check">
                                <input
                                    id={`${idPrefix}-opt-out-yes`}
                                    type="radio"
                                    className="form-check-input"
                                    name={`${idPrefix}-opt-out`}
                                    checked={!mixedFields.allowOptOutMidCohort && draft.allowOptOutMidCohort === true}
                                    onChange={() => update({ ...draft, allowOptOutMidCohort: true }, ['allowOptOutMidCohort'])}
                                />
                                <label className="form-check-label" htmlFor={`${idPrefix}-opt-out-yes`}>
                                    Yes
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    id={`${idPrefix}-opt-out-no`}
                                    type="radio"
                                    className="form-check-input"
                                    name={`${idPrefix}-opt-out`}
                                    checked={!mixedFields.allowOptOutMidCohort && draft.allowOptOutMidCohort !== true}
                                    onChange={() => update({ ...draft, allowOptOutMidCohort: false }, ['allowOptOutMidCohort'])}
                                />
                                <label className="form-check-label" htmlFor={`${idPrefix}-opt-out-no`}>
                                    No
                                </label>
                            </div>
                        </div>
                        {mixedFields.allowOptOutMidCohort ? (
                            <div className="small text-muted mt-1">{VARIES_BY_CAMPUS}</div>
                        ) : null}
                    </div>
                    <div className="col-md-4">
                        <div className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1">Allow Mentees to request for mentor change</div>
                        <div className="d-flex gap-3 mt-1">
                            <div className="form-check">
                                <input
                                    id={`${idPrefix}-mentor-change-yes`}
                                    type="radio"
                                    className="form-check-input"
                                    name={`${idPrefix}-mentor-change`}
                                    checked={!mixedFields.allowMentorChangeRequest && draft.allowMentorChangeRequest === true}
                                    onChange={() =>
                                        update({ ...draft, allowMentorChangeRequest: true }, ['allowMentorChangeRequest'])
                                    }
                                />
                                <label className="form-check-label" htmlFor={`${idPrefix}-mentor-change-yes`}>
                                    Yes
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    id={`${idPrefix}-mentor-change-no`}
                                    type="radio"
                                    className="form-check-input"
                                    name={`${idPrefix}-mentor-change`}
                                    checked={!mixedFields.allowMentorChangeRequest && draft.allowMentorChangeRequest !== true}
                                    onChange={() =>
                                        update({ ...draft, allowMentorChangeRequest: false }, ['allowMentorChangeRequest'])
                                    }
                                />
                                <label className="form-check-label" htmlFor={`${idPrefix}-mentor-change-no`}>
                                    No
                                </label>
                            </div>
                        </div>
                        {mixedFields.allowMentorChangeRequest ? (
                            <div className="small text-muted mt-1">{VARIES_BY_CAMPUS}</div>
                        ) : null}
                    </div>
                </div>
            </fieldset>
        </>
    );

    if (!title) {
        return body;
    }

    return (
        <div className="card border-0 rounded-3 shadow-sm bg-white mb-3">
            <h2 className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                <button
                    className={`accordion-button bg-transparent p-0 shadow-none font-14 font-700 text-dark ${expanded ? '' : 'collapsed'}`}
                    type="button"
                    onClick={onToggle}>
                    🏛️ {title}
                </button>
            </h2>
            <div className={`card-body p-4 ${expanded ? 'd-block' : 'd-none'}`}>{body}</div>
        </div>
    );
};

export default MentorshipCampusConfig;
